// src/components/admin/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaUpload, FaImages, FaCalendarAlt, FaComments, FaTachometerAlt, FaBars, FaTimes, FaKey } from 'react-icons/fa'; // Added FaKey icon

const API_BASE = process.env.REACT_APP_API_URL;

const AdminDashboard = () => {
  const location = useLocation();
  const [appointmentCount, setAppointmentCount] = useState(0);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // State for mobile menu visibility

  // Function to fetch counts for new appointments and feedback
  const fetchCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('AdminDashboard: No token found, cannot fetch counts. User might not be logged in or token expired.');
        setAppointmentCount(0); // Ensure counts are reset if no token
        setFeedbackCount(0);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Fetch New Appointment Count
      const apptRes = await axios.get(`${API_BASE}/api/appointments/count/new`, { headers, withCredentials: true });
      setAppointmentCount(apptRes.data.count);
      console.log('AdminDashboard: New Appointment Count:', apptRes.data.count);

      // Fetch New Feedback Count
      const fbRes = await axios.get(`${API_BASE}/api/feedback/count/new`, { headers, withCredentials: true });
      setFeedbackCount(fbRes.data.count);
      console.log('AdminDashboard: New Feedback Count:', fbRes.data.count);

    } catch (err) {
      console.error('AdminDashboard: Error fetching counts:', err.response?.data || err.message);
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.error('AdminDashboard: Authentication/Authorization failed for count endpoints. Ensure admin user is logged in and backend is running correctly.');
      } else if (err.response?.status === 404) {
        console.error('AdminDashboard: Count API routes not found. Check server.js and backend route files for /count/new endpoints.');
      } else {
        console.error('AdminDashboard: Network or server error fetching counts.');
      }
      setAppointmentCount(0); // Reset counts on error
      setFeedbackCount(0);
    }
  }, []);

  useEffect(() => {
    fetchCounts(); // Fetch counts on component mount

    // Set up an interval to refresh counts periodically (e.g., every minute)
    const interval = setInterval(fetchCounts, 60000); // Refreshes every 60 seconds

    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Close menu when route changes (for mobile sidebar)
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Determine active link for styling
  const isActive = (path) => location.pathname === path || (path === '/admin/dashboard' && location.pathname === '/admin');

  return (
    <div className="min-h-screen bg-gray-100 font-inter md:grid md:grid-cols-[auto_1fr] md:overflow-hidden">
      {/* Top Bar for Mobile/Tablet */}
      <header className="bg-gray-800 text-white p-4 flex justify-between items-center shadow-lg md:hidden">
        <h2 className="text-xl font-bold text-cyan-300">Admin Panel</h2>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="text-white text-2xl p-2 rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </header>

      {/* Sidebar Navigation - Desktop view (md:relative) and Mobile Overlay (fixed) */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-gray-800 text-white p-6 flex flex-col shadow-lg z-50 transform transition-transform duration-300
          ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:relative md:translate-x-0 md:min-h-screen md:flex md:flex-col
        `}
      >
        <h2 className="text-2xl font-bold mb-6 text-cyan-300 hidden md:block">Admin Panel</h2>
        <nav className="flex-grow">
          <ul className="space-y-4">
            <li>
              <Link
                to="/admin/dashboard"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition duration-200
                  ${isActive('/admin/dashboard') ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-gray-700 text-gray-300'}
                `}
              >
                <FaTachometerAlt /> Dashboard
              </Link>
            </li>
            <li>
              <Link
                to="/admin/upload-image"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition duration-200
                  ${isActive('/admin/upload-image') ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-gray-700 text-gray-300'}
                `}
              >
                <FaUpload /> Upload Image
              </Link>
            </li>
            <li>
              <Link
                to="/admin/image-manager"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition duration-200
                  ${isActive('/admin/image-manager') ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-gray-700 text-gray-300'}
                `}
              >
                <FaImages /> Image Manager
              </Link>
            </li>
            <li>
              <Link
                to="/admin/appointments"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition duration-200 relative
                  ${isActive('/admin/appointments') ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-gray-700 text-gray-300'}
                `}
              >
                <FaCalendarAlt /> Appointments
                {/* Conditional rendering for appointment count badge */}
                {appointmentCount > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center absolute -top-1 -right-1">
                    {appointmentCount}
                  </span>
                )}
              </Link>
            </li>
            <li>
              <Link
                to="/admin/feedback"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition duration-200 relative
                  ${isActive('/admin/feedback') ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-gray-700 text-gray-300'}
                `}
              >
                <FaComments /> Feedback
                {/* Conditional rendering for feedback count badge */}
                {feedbackCount > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center absolute -top-1 -right-1">
                    {feedbackCount}
                  </span>
                )}
              </Link>
            </li>
            {/* NEW: Admin Change Password Link */}
            <li>
              <Link
                to="/admin/change-password"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition duration-200
                  ${isActive('/admin/change-password') ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-gray-700 text-gray-300'}
                `}
              >
                <FaKey /> Change Password
              </Link>
            </li>
          </ul>
        </nav>
        {/* Logout Link */}
        <div className="mt-auto pt-4 border-t border-gray-700">
          <Link
            to="/logout"
            className="flex items-center gap-3 px-4 py-2 rounded-lg transition duration-200 text-red-400 hover:bg-gray-700"
          >
            Logout
          </Link>
        </div>
      </aside>

      {/* Overlay for mobile menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        ></div>
      )}

      {/* Main Content Area */}
      <main className="flex-grow p-8 bg-gray-100 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminDashboard;